import { createContext, useContext, ReactNode, useCallback, useMemo, useState, useRef } from 'react';
import { Building, BuildingOrg, Group, EcsDevice, VideoCall, CallId, UtcTime, DeviceId, GroupId, EcsProperties } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { push as navigateTo } from 'connected-react-router';
import { callApi } from 'apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { ecsCallsFetched, receiveEcsCallMessage, sendEcsMSCallMessage } from 'actions/ecsActions';
import { buildingFetched } from 'actions/buildingActions';
import { useWebSocket } from 'hooks';
import { getWebSocketConnected } from 'selectors/websocketSelectors';
import { renameDevices } from 'logic/renameDevices';
import { toError } from 'shared/util/toError';
import { keyBy } from 'lodash';

export type ExternalIdStatus = {
    externalId: string | null; // Will be null on an elevator that doesn't have an external ID
    building: Building;
    buildingOrgs: BuildingOrg[];
    gatewayDeviceId: DeviceId;
    group: Group;
    devices: EcsDevice[]; // Devices in the group (note: even devices that are now offline -- e.g. replaced)
    calls: VideoCall[];
    nextToken: string | null;
    ecsProperties: EcsProperties | null;
};


export type ElevatorState = {
    saving: boolean;
    loading: boolean;
    loadingImage: boolean;
    imageData: string | null;
    imageError: string | null;
    imageTimestamp: UtcTime;
    gatewayDeviceId: DeviceId | null;
    building: Building | null;
    externalId: string | null;
    group: Group | null;
    devices: EcsDevice[] | null;
    pendingOrder: CallId[];
    activeOrder: CallId[];
    finishedOrder: CallId[];
    nextToken: string | null;
    errorMessage: string | null;
    triggerResetDisplayName: number;
    ecsProperties: EcsProperties | null;
    load(externalId: string, action: string | null): Promise<void>;
    changeName(displayName: string): Promise<void>;
    changeExternalId(externalId: string): Promise<void>;
    refreshImage(): void;
    requestCall(): void;
    detachElevator(): void;
    unload(): void;
};

export const ElevatorContext = createContext<ElevatorState>(undefined as any);

export type Props = {
    children?: ReactNode;
};

export const ElevatorProvider = ({ children }: Props) => {
    const { t } = useTranslation();
    const websocketClient = useWebSocket();

    const websocketConnected = useSelector(getWebSocketConnected);

    const [ loading, setLoading ] = useState(true);
    const [ loadingImage, setLoadingImage ] = useState(true);
    const [ saving, setSaving ] = useState(false);
    const [ building, setBuilding ] = useState<Building | null>(null);
    const [ devices, setDevices ] = useState<EcsDevice[] | null>(null);
    const [ gatewayDeviceId, setGatewayDeviceId ] = useState<DeviceId | null>(null);
    const [ group, setGroup ] = useState<Group | null>(null);
    const [ externalId, setExternalId ] = useState<string | null>(null);
    const [ calls, setCalls ] = useState<VideoCall[] | null>(null);
    const [ nextToken, setNextToken ] = useState<string | null>(null);
    const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
    const [ triggerResetDisplayName, setTriggerResetDisplayName ] = useState<number>(0);
    const [ ecsProperties, setEcsProperties ] = useState<EcsProperties | null>(null);
    const [ imageData, setImageData ] = useState<string | null>(null);
    const [ imageTimestamp, setImageTimestamp ] = useState<UtcTime>(0);
    const [ imageError, setImageError ] = useState<string | null>(null);
    const refreshIntervalRef = useRef<NodeJS.Timeout>();

    const dispatch = useDispatch();

    const groupId = group && group.id;
    
    //
    // TODO: move this common call code into a dedicated file
    //       (eliminate duplication between here and VideoListPage)
    //
    const [ pendingOrder, setPendingOrder ] = useState<CallId[]>([]);
    const [ activeOrder, setActiveOrder ] = useState<CallId[]>([]);
    const [ finishedOrder, setFinishedOrder ] = useState<CallId[]>([]);

    const MAX_AGE = 12 * 60 * 60 * 1000; // Twelve hours
    const UNANSWERED_CALL_EXPIRY = 15 * 60 * 1000; // 15 minutes
    const MAX_NO_UPDATES_AGE = 30 * 60 * 1000; // 30 minutes

    const handleTick = useCallback((calls: VideoCall[]) => {
        const now = Date.now();

        setPendingOrder(
            calls
                .filter(c =>
                    !c.joined &&
                    !c.ended &&
                    c.source === 'EL' &&
                    (now - c.created < UNANSWERED_CALL_EXPIRY) // hasn't expired
                )
                .sort((a, b) => a.created - b.created) // First-created first
                .map(c => c.callId)
        );
    
        setActiveOrder(
            calls
                .filter(c =>
                    !!c.joined &&
                    !c.ended &&
                    (now - c.created < MAX_AGE) &&
                    (now - c.updated < MAX_NO_UPDATES_AGE)
                )
                .sort((a, b) => b.joined! - a.joined!) // Most-recently-joined first
                .map(c => c.callId)
        );
    
        setFinishedOrder(
            calls
                .filter(c =>
                    (now - c.created < MAX_AGE) &&
                    (
                        !!c.ended ||
                        (
                            !c.joined &&
                            !c.ended &&
                            (now - c.created >= UNANSWERED_CALL_EXPIRY) // has expired
                        )
                    )
                )
                .sort((a, b) =>
                    // Most-recently-ended first (or if unanswered, when the call was created)
                    (b.ended || b.created) - (a.ended || a.created)
                )
                .map(c => c.callId)
        );
    }, [ setPendingOrder, setActiveOrder, setFinishedOrder, MAX_AGE, UNANSWERED_CALL_EXPIRY, MAX_NO_UPDATES_AGE ]);

    /* TODO: need to prevent the order arrays from changing every second unless they actually change
    useEffect(() => {
        // Do one page refresh now since the video calls list is new
        handleTick(calls || []);

        // Refresh the page every second when there are calls on the page.
        // (i.e. no need to refresh if the page is empty)
        if (calls && calls.length > 0) {
            refreshIntervalRef.current = setInterval(() => handleTick(calls || []), 1000);
        }
        return () => clearInterval(refreshIntervalRef.current!);
    }, [ calls, refreshIntervalRef, handleTick ]);
    */

    const requestCallForElevator = useCallback((elevatorId: GroupId | null, enqueueIfNotOpen: boolean) => {
        if (!elevatorId) {
            console.error('No elevator specified');
            return;
        }
        dispatch(sendEcsMSCallMessage(elevatorId, { sent: Date.now() }));
        websocketClient.ecsCall(elevatorId, enqueueIfNotOpen);
    }, [ websocketClient, dispatch ]);

    const load = useCallback(async (externalId: string, action: string | null) => {
        console.log(`Loading elevator ${externalId}${action ? ` (${action})` : ''}`);
        setLoading(true);
        try {
            const {
                externalId: actualExternalId, building, buildingOrgs, gatewayDeviceId, group, devices, calls, nextToken, ecsProperties,
            }: ExternalIdStatus = await callApi('GET', `/api/extids/${externalId}/status`);

            // Send the calls to the Redux state so they can appear in other parts of the app (e.g. such as the call screen)
            dispatch(buildingFetched({ building, buildingOrgs, devices }));
            dispatch(ecsCallsFetched(calls));

            setExternalId(actualExternalId);
            setErrorMessage(null);
            setGroup(group);
            setGatewayDeviceId(gatewayDeviceId);
            setBuilding(building);
            setDevices(devices);
            setCalls(calls);
            setNextToken(nextToken);
            setEcsProperties(ecsProperties);

            if (action === 'call') {
                const pendingCalls = calls ? calls.filter(c => c.source === 'EL' && !c.joined && !c.ended) : [];
                if (pendingCalls.length) {
                    const { callId } = pendingCalls[0];
                    console.log(`Redirecting to call ${callId}`);
                    dispatch(navigateTo(`/video/${callId}`));
                    return;
                }
                requestCallForElevator(group && group.id, true);
                return;
            }
            else if (action) {
                console.log(`Ignoring action "${action}"`);
            }
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(t([ `VIDEO.ERROR.${err.message}`, 'SERVER_ERROR' ]));
        }
        finally {
            setLoading(false);
        }
    }, [ setLoading, setExternalId, setErrorMessage, setGatewayDeviceId, setGroup, setBuilding, setDevices, setCalls, setNextToken, setEcsProperties, requestCallForElevator, dispatch, t ]);

    //
    // Used in Factory Programming buildings once an elevator has been configured at the factory
    // and it now ready to be shipped to the building and installed on the network there.
    //
    const detachElevator = useCallback(async () => {
        if (!gatewayDeviceId || !group || !group.id || !building) {
            throw new Error('Elevator has not been loaded yet');
        }
        setSaving(true);
        setErrorMessage(null);
        try {
            await callApi('POST', `/api/elevators/${gatewayDeviceId}/${group.id}/detach`);
            dispatch(navigateTo(`/buildings/${building.urlSlug}/elevators`));
        }
        catch (e) {
            const err = toError(e);
            setErrorMessage(t([ `ELEVATOR.ERROR.${err.message}`, `ERROR.${err.message}`, 'SERVER_ERROR' ]));
        }
        finally {
            setSaving(false);
        }
    }, [ gatewayDeviceId, group, building, setSaving, setErrorMessage, dispatch, t ]);

    // KILL? this should be unnecessary from the other clean up of useEffect where the interval is set
    const unload = useCallback(() => {
        clearInterval(refreshIntervalRef.current!);
    }, [ refreshIntervalRef ]);

    //
    // Helper method to call the API on the server to update the elevator
    //
    const changeElevator = useCallback(async (payload: Record<string, unknown>): Promise<boolean> => {
        if (!groupId) return false;
        if (!gatewayDeviceId) return false;

        setSaving(true);
        try {
            return await callApi('PUT', `/api/elevators/${gatewayDeviceId}/${groupId}`, payload);
        }
        catch (e) {
            const err = toError(e);
            console.error(err);
            setTriggerResetDisplayName(triggerResetDisplayName => triggerResetDisplayName + 1);
            setErrorMessage(t([ `ELEVATOR.ERROR.${err.message}`, 'SERVER_ERROR' ]));
            return false;
        }
        finally {
            setSaving(false);
        }
    }, [ setSaving, t, gatewayDeviceId, groupId, setTriggerResetDisplayName ]);

    //
    // Change the elevator name on the server
    //
    const changeName = useCallback(async (displayName: string) => {
        const deviceNames = renameDevices(displayName, keyBy(devices, 'deviceId'), t);
        
        const success = await changeElevator({
            displayName,
            deviceNames,
        });

        if (success) {
            setGroup(group => ({
                ...group!,
                displayName,
            }));

            setDevices(devices => devices!.map(d => ({
                ...d,
                displayName: deviceNames[d.deviceId],
            })));
        }
    }, [ devices, changeElevator, setGroup, setDevices, t ]);

    //
    // Change the elevator external ID on the server
    //
    const changeExternalId = useCallback(async (externalId: string): Promise<void> => {
        const success = await changeElevator({
            externalId,
        });
        
        // Navigate to the new page URL if the external ID changed
        if (success) {
            setExternalId(externalId);
            dispatch(navigateTo(`/elevator/${externalId || groupId}`));
        }
    }, [ groupId, changeElevator, setExternalId, dispatch ]);

    //
    // Request the Gateway to fetch a new image from the camera in this elevator
    //
    const refreshImage = useCallback(async () => {
        setLoadingImage(true);
        try {
            // TODO: send websocket command (or API call?)
            setImageData(null);
        }
        catch (e) {
            const err = toError(e);
            console.error(err);
            setImageError(t([ `ELEVATOR.ERROR.${err.message}`, 'SERVER_ERROR' ]));
        }
        finally {
            setLoadingImage(false);
        }
    }, [ setLoadingImage, setImageData, setImageTimestamp, setImageError, t ]);
    
    //
    // Attempt to initiate an unsolicited video call from the Monitoring Station
    // to the current elevator (from the load() method).
    //
    const requestCall = useCallback(async () => {
        if (!websocketConnected) {
            console.warn('websocket is not connected');
            return;
        }

        // Check the server for the most-recent pending call data
        let pendingCall: VideoCall | null = null;
        try {
            const cacheBust = Math.random().toString(28).substring(2);
            pendingCall = await callApi('GET', `/api/video/elevator/${gatewayDeviceId}/${groupId}/pending?${cacheBust}`);
            if (pendingCall) {
                dispatch(receiveEcsCallMessage(pendingCall));
            }
        }
        catch (err) {
            console.error('Failed to check for pending calls', err);
        }

        // Answer the pending call, if one exists
        if (pendingCall) {
            const { callId } = pendingCall;
            console.log(`Redirecting to call ${callId}`);
            dispatch(navigateTo(`/video/${callId}`));
            return;
        }

        // Start a new call
        requestCallForElevator(groupId, false);
    }, [ requestCallForElevator, groupId, calls, gatewayDeviceId, websocketConnected, dispatch ]);


    const elevatorState = useMemo<ElevatorState>(() => Object.freeze({
        saving,
        loading,
        loadingImage,
        imageData,
        imageTimestamp,
        imageError,
        gatewayDeviceId,
        externalId,
        building,
        group,
        devices,
        pendingOrder,
        activeOrder,
        finishedOrder,
        nextToken,
        errorMessage,
        triggerResetDisplayName,
        ecsProperties,
        load,
        changeName,
        changeExternalId,
        refreshImage,
        requestCall,
        detachElevator,
        unload,
    }), [ saving, loading, loadingImage, imageData, imageTimestamp, imageError, gatewayDeviceId, externalId, building, group, devices, pendingOrder, activeOrder, finishedOrder, nextToken, errorMessage, triggerResetDisplayName, ecsProperties, load, changeName, changeExternalId, refreshImage, requestCall, detachElevator, unload ]);

    return (
        <ElevatorContext.Provider value={ elevatorState }>
            { children }
        </ElevatorContext.Provider>
    );
};

export const useElevator = () => {
    return useContext(ElevatorContext);
};
